// src/features/agent-builder/components/SkillCreatorModal.tsx
import React, { useState, useEffect, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  Button,
  TextField,
  TextareaField,
  TabbedComponent,
  CodeEditorField,
} from '@dopomogai/ui';
import type { CustomSkillFormData } from '@dopomogai/types'; // UPDATED IMPORT
import { AIToolsmithTeaser } from '../teasers/AIToolsmithTeaser';

interface SkillCreatorModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSaveSkill: (skillData: CustomSkillFormData) => void;
    initialSkillData?: Partial<CustomSkillFormData>;
  }
  
  const initialFormState: CustomSkillFormData = {
    name: '',
    description: '',
    pythonScript: '# Your Python script here\n# It should define a function that will be called.\n# Example:\n# def handler(params):\n#   city = params.get("city", "London")\n#   # ... fetch weather ...\n#   return {"temperature": 20, "condition": "Sunny", "city": city}',
    aiToolsmithDescription: '',
  };

const TABS_CONFIG = [
  {
    id: 'manual-skill-tab',
    labelKey: 'agentBuilder:skillModal.tabManual',
  },
  {
    id: 'ai-toolsmith-tab',
    labelKey: 'agentBuilder:skillModal.tabAIToolsmith',
  },
];


export const SkillCreatorModal: React.FC<SkillCreatorModalProps> = ({
  isOpen,
  onClose,
  onSaveSkill,
  initialSkillData,
}) => {
  const { t } = useTranslation('agentBuilder');
  // Corrected: State holds the active tab ID (string)
  const [activeTabId, setActiveTabId] = useState<string>(TABS_CONFIG[0].id);
  const [formData, setFormData] = useState<CustomSkillFormData>({
    ...initialFormState,
    ...initialSkillData,
  });

  useEffect(() => {
    if (isOpen) {
      setFormData({ ...initialFormState, ...initialSkillData });
      setActiveTabId(TABS_CONFIG[0].id); // Reset to first tab's ID when opening
    } else {
      setFormData(initialFormState);
    }
  }, [isOpen, initialSkillData]);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCodeChange = (value: string | undefined) => {
    setFormData((prev) => ({ ...prev, pythonScript: value || '' }));
  };

  const handleAiDescriptionChange = (value: string) => {
     setFormData((prev) => ({ ...prev, aiToolsmithDescription: value }));
  };

  const handleSave = () => {
    if (!formData.name.trim() || !formData.description.trim()) {
        alert(t('skillModal.validation.nameAndDescriptionRequired'));
        return;
    }
    // Check based on activeTabId
    if (activeTabId === 'manual-skill-tab' && !formData.pythonScript.trim()) {
        alert(t('skillModal.validation.pythonScriptRequired'));
        return;
    }
    onSaveSkill(formData);
    onClose();
  };

  // Dynamically create tabs with content based on current formData and activeTabId
  const getTabsWithContent = () => [
    {
      id: TABS_CONFIG[0].id,
      labelKey: TABS_CONFIG[0].labelKey,
      content: (
        <div className="py-4">
          <CodeEditorField
            labelKey="agentBuilder:skillModal.manual.pythonScript.label"
            value={formData.pythonScript}
            onChange={handleCodeChange}
            language="python"
            height="250px"
          />
           <p className="mt-2 text-xs text-slate-500">
             {t('skillModal.manual.pythonNote')}
           </p>
        </div>
      ),
    },
    {
      id: TABS_CONFIG[1].id,
      labelKey: TABS_CONFIG[1].labelKey,
      content: (
        <AIToolsmithTeaser
          description={formData.aiToolsmithDescription}
          onDescriptionChange={handleAiDescriptionChange}
        />
      ),
    },
  ];


  const modalFooter = (
    <div className="flex justify-end gap-3">
      <Button
        textKey="agentBuilder:common.cancel"
        variant="outline"
        onClick={onClose}
      />
      <Button
        textKey="agentBuilder:skillModal.saveButton"
        variant="primary"
        onClick={handleSave}
      />
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      titleKey="agentBuilder:skillModal.title"
      size="3xl"
      footer={modalFooter}
    >
      <div className="space-y-4 pr-1 mr-[-4px] max-h-[65vh] overflow-y-auto custom-scrollbar">
        <TextField
          labelKey="agentBuilder:skillModal.skillName.label"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder={t('skillModal.skillName.placeholder')}
          required
        />
        <TextareaField
          labelKey="agentBuilder:skillModal.skillDescription.label"
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder={t('skillModal.skillDescription.placeholder')}
          rows={4}
          required
          descriptionTextKey="agentBuilder:skillModal.skillDescription.helpText"
        />
        <TabbedComponent
          tabs={getTabsWithContent()}
          activeTabId={activeTabId} // Corrected: use activeTabId
          onTabChange={setActiveTabId} // Corrected: Directly pass setter if signature matches (tabId: string) => void
        />
      </div>
    </Modal>
  );
};